import isNumber from "lodash-es/isNumber";

import { randomUUID } from "./crypto";

const EPSILON = 0.03;

export function isCloseTo(
  number: number,
  comparator: number,
  epsilon: number = EPSILON,
) {
  return Math.abs(number - comparator) < epsilon;
}

export function round(number: number, precision: number) {
  return Number.parseFloat(Math.round(number).toFixed(precision));
}

/**
 * Rounds a number to a given number of decimal places.
 *
 * Unlike {@link round} (which rounds to an integer before applying precision),
 * this rounds to the requested decimal precision, e.g.
 * `roundToPrecision({ value: 1.2345, precision: 2 })` → `1.23`.
 */
export function roundToPrecision({
  value,
  precision,
}: {
  value: number;
  precision: number;
}): number {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

export function clamp(
  number: number,
  minimum: number,
  maximum: number,
): number {
  const _minimum = Math.min(minimum, maximum);
  const _maximum = Math.max(minimum, maximum);
  return Math.min(Math.max(_minimum, number), _maximum);
}

export function parseInteger(value: string | number) {
  let number: number;
  if (isNumber(value) && Number.isInteger(value)) {
    number = value;
  } else {
    // NOTE (Chance 2024-05-23): Passing a number value to parseInt is safe. No
    // reason to cast to a string. We still want to attempt to "parse"
    // non-integer numbers, as floats will be truncated and infinite values
    // returned as NaN.
    // Legacy cast - may or may not be necessary
    // oxlint-disable-next-line replo/no-as-cast, typescript/no-explicit-any -- legacy any/cast, may or may not be needed
    number = Number.parseInt(value as any, 10);
  }
  // NOTE (Chance 2024-01-12): 0 eq check removes -0 as a possible return value
  return number === 0 ? 0 : number;
}

export function parseIntegerOrDefault(value: string, defaultValue: number) {
  const result = Number.parseInt(value);
  if (Number.isNaN(result)) {
    return defaultValue;
  }
  return result;
}

export function parseFloat(value: string | number) {
  const number = isNumber(value) ? value : Number.parseFloat(value);
  // NOTE (Chance 2024-01-12): 0 eq check removes -0 as a possible return value
  return number === 0 ? 0 : number;
}

export function parseFloatOrNull(
  value: string | null | undefined,
): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  const num = Number.parseFloat(value);
  return Number.isNaN(num) ? null : num;
}

/**
 * Converts cents to dollars.
 * Commonly used for payment processor APIs (Stripe, Dub, etc.) that return amounts in cents.
 *
 * @param cents - Amount in cents
 * @returns Amount in dollars
 *
 * @example
 * centsToDollars(12345) // 123.45
 * centsToDollars(5000) // 50
 * centsToDollars(0) // 0
 */
export function centsToDollars(cents: number): number {
  return cents / 100;
}

/**
 * @returns Distance between two points
 */
export function distance(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
}

export function formatWithCommasAndTwoDecimals(
  num: number | undefined,
  shouldShowDecimals: boolean,
): string {
  if (num === undefined) {
    return shouldShowDecimals ? "0.00" : "0";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: shouldShowDecimals ? 2 : 0,
    maximumFractionDigits: shouldShowDecimals ? 2 : 0,
  }).format(Math.abs(num));
}

export function randomBigNumber() {
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
}

export function randomN(i: number) {
  return Math.floor(Math.random() * i);
}

export function randomBigString() {
  return randomUUID().replaceAll("-", "");
}

/**
 * Given a current value and a previous value, calculate the percentage change
 * from the previous value to the current value.
 *
 * @author Max 2025-04-01
 */
export function calculatePercentageChange(current: number, previous: number) {
  if (previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

/**
 * Format bytes into a human readable string, favoring the largest unit possible.
 *
 * @param bytes - The number of bytes (can be a number or numeric string)
 * @returns Human readable string (e.g., "1.50 GB", "256.00 MB", "1024 bytes")
 *
 * @example
 * formatBytes(1536) // "1.50 KB"
 * formatBytes(1048576) // "1.00 MB"
 * formatBytes("1073741824") // "1.00 GB"
 *
 * @author Max 2024-08-28
 */
export function formatBytes(bytes: number | string): string {
  const bytesNum = typeof bytes === "string" ? Number(bytes) : bytes;

  if (bytesNum >= GB) {
    return `${(bytesNum / GB).toFixed(2)} GB`;
  }
  if (bytesNum >= MB) {
    return `${(bytesNum / MB).toFixed(2)} MB`;
  }
  if (bytesNum >= KB) {
    return `${(bytesNum / KB).toFixed(2)} KB`;
  }
  return `${bytesNum} bytes`;
}
